import React, { useState, useEffect } from 'react';
import { getFromStorage, saveToStorage } from '../utils/storage';
import { getAvoNetworks, getAvoNetworkStatus } from '../utils/avoUtils';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Container, Box, Typography, Card, CardContent, Button, AppBar, Toolbar,
  IconButton, CircularProgress, Fade, Chip, Stack, Menu, MenuItem, ListItemIcon,
  ListItemText, Avatar, Badge, Tooltip, styled
} from '@mui/material';
import {
  Hub, DeveloperMode, BugReport, Rocket, Menu as MenuIcon, 
  CheckCircle, Error, Warning, Refresh, Home, ArrowBack
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';

// Styled components with AVO branding
const AvoCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  backgroundColor: '#0a0a0a',
  border: '1px solid #333333',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 48px rgba(255, 255, 255, 0.1)',
    borderColor: '#555555'
  }
}));

const NetworkButton = styled(Button)(({ theme, selected }) => ({
  background: selected 
    ? 'linear-gradient(135deg, #ffffff 0%, #e0e0e0 100%)'
    : 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
  backgroundSize: '200% auto',
  color: selected ? '#000000' : '#ffffff',
  border: selected ? '2px solid #ffffff' : '1px solid #444444',
  borderRadius: '12px',
  padding: '16px 24px',
  fontWeight: 600,
  textTransform: 'none',
  minHeight: '80px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundPosition: 'right center',
    transform: 'translateY(-2px)',
    boxShadow: selected 
      ? '0 8px 24px rgba(255, 255, 255, 0.3)'
      : '0 8px 24px rgba(255, 255, 255, 0.1)',
  },
  '&:active': {
    transform: 'translateY(0)'
  }
}));

const StatusChip = styled(Chip)(({ status }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  ...(status === 'connected' && {
    backgroundColor: '#1b5e20',
    color: '#ffffff',
    '& .MuiChip-icon': { color: '#4caf50' }
  }),
  ...(status === 'disconnected' && {
    backgroundColor: '#b71c1c',
    color: '#ffffff',
    '& .MuiChip-icon': { color: '#f44336' }
  }),
  ...(status === 'loading' && {
    backgroundColor: '#e65100',
    color: '#ffffff',
    '& .MuiChip-icon': { color: '#ff9800' }
  })
}));

const AvoNetworkSelector = () => {
  const [currentNetwork, setCurrentNetwork] = useState('devnet');
  const [networkStatus, setNetworkStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const networks = getAvoNetworks();

  // Network icons mapping
  const getNetworkIcon = (networkKey) => {
    const icons = {
      devnet: <DeveloperMode />,
      testnet: <BugReport />,
      mainnet: <Rocket />
    };
    return icons[networkKey] || <Hub />;
  };

  // Network descriptions
  const getNetworkDescription = (networkKey) => {
    const descriptions = {
      devnet: 'Local development network for testing',
      testnet: 'Public test network for development',
      mainnet: 'Production network (coming soon)'
    };
    return descriptions[networkKey] || 'AVO Network';
  };

  useEffect(() => {
    loadCurrentNetwork();
    checkAllNetworkStatus();
  }, []);

  const loadCurrentNetwork = async () => {
    try {
      const savedNetwork = await getFromStorage('currentAvoNetwork');
      if (savedNetwork && networks[savedNetwork]) {
        setCurrentNetwork(savedNetwork);
      }
    } catch (err) {
      console.error('Error loading current network:', err);
    }
  };

  const checkAllNetworkStatus = async () => {
    setRefreshing(true);
    const statusPromises = Object.keys(networks).map(async (networkKey) => {
      try {
        const status = await getAvoNetworkStatus(networkKey);
        return { networkKey, status };
      } catch (err) {
        return { 
          networkKey, 
          status: { 
            network: networkKey, 
            connected: false, 
            error: err.message 
          } 
        };
      }
    });

    try {
      const results = await Promise.all(statusPromises);
      const statusMap = {};
      results.forEach(({ networkKey, status }) => {
        statusMap[networkKey] = status;
      });
      setNetworkStatus(statusMap);
    } catch (err) {
      console.error('Error checking network status:', err);
      toast.error('Failed to check network status');
    } finally {
      setRefreshing(false);
    }
  };

  const switchNetwork = async (networkKey) => {
    if (networkKey === currentNetwork) return;

    setLoading(true);
    try {
      // Save new network
      await saveToStorage('currentAvoNetwork', networkKey);
      setCurrentNetwork(networkKey);
      
      // Check status of new network
      const status = await getAvoNetworkStatus(networkKey);
      setNetworkStatus(prev => ({ ...prev, [networkKey]: status }));
      
      toast.success(`🚀 Switched to AVO ${networks[networkKey].name}`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Navigate back to dashboard after successful switch
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
      
    } catch (err) {
      console.error('Error switching network:', err);
      toast.error(`Failed to switch to ${networks[networkKey].name}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (networkKey) => {
    const status = networkStatus[networkKey];
    if (!status) {
      return { 
        label: 'Checking...', 
        status: 'loading', 
        icon: <CircularProgress size={16} /> 
      };
    }
    
    if (status.connected) {
      return { 
        label: 'Connected', 
        status: 'connected', 
        icon: <CheckCircle /> 
      };
    } else {
      return { 
        label: status.error ? 'Error' : 'Offline', 
        status: 'disconnected', 
        icon: <Error /> 
      };
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <AppBar position="static" sx={{ 
        backgroundColor: '#000000', 
        borderBottom: '1px solid #FFFFFF',
        borderRadius: 0,
        mb: 3
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            component={Link}
            to="/dashboard"
            sx={{ 
              mr: 2,
              color: '#FFFFFF',
              '&:hover': {
                bgcolor: '#FFFFFF',
                color: '#000000'
              }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, color: '#FFFFFF' }}>
            🚀 AVO Network Selector
          </Typography>
          <Tooltip title="Refresh network status">
            <IconButton 
              color="inherit"
              onClick={checkAllNetworkStatus}
              disabled={refreshing}
              sx={{
                color: '#FFFFFF',
                '&:hover': {
                  bgcolor: '#FFFFFF',
                  color: '#000000'
                }
              }}
            >
              {refreshing ? <CircularProgress size={24} sx={{ color: '#FFFFFF' }} /> : <Refresh />}
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Network Selection */}
      <AvoCard>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #ffffff 0%, #cccccc 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 3
          }}>
            Select AVO Network
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Choose the AVO network you want to connect to. Currently connected to: <strong>{networks[currentNetwork]?.name}</strong>
          </Typography>

          <Stack spacing={3}>
            {Object.entries(networks).map(([networkKey, networkConfig]) => {
              const isSelected = networkKey === currentNetwork;
              const statusInfo = getStatusInfo(networkKey);
              const isAvailable = networkKey === 'devnet' || networkConfig.rpcUrl.includes('localhost');

              return (
                <Fade in={true} key={networkKey} timeout={300}>
                  <Box>
                    <NetworkButton
                      selected={isSelected}
                      onClick={() => switchNetwork(networkKey)}
                      disabled={loading || !isAvailable}
                      fullWidth
                      sx={{ 
                        justifyContent: 'flex-start',
                        opacity: isAvailable ? 1 : 0.5
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                        <Avatar sx={{ 
                          bgcolor: isSelected ? '#000000' : '#333333',
                          color: isSelected ? '#ffffff' : '#ffffff'
                        }}>
                          {getNetworkIcon(networkKey)}
                        </Avatar>
                        
                        <Box sx={{ flexGrow: 1, textAlign: 'left' }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {networkConfig.name}
                            </Typography>
                            {isSelected && (
                              <Chip 
                                label="ACTIVE" 
                                size="small" 
                                sx={{ 
                                  bgcolor: '#000000', 
                                  color: '#ffffff',
                                  fontWeight: 600
                                }} 
                              />
                            )}
                            {!isAvailable && (
                              <Chip 
                                label="COMING SOON" 
                                size="small" 
                                sx={{ 
                                  bgcolor: '#666666', 
                                  color: '#ffffff',
                                  fontWeight: 600
                                }} 
                              />
                            )}
                          </Stack>
                          <Typography variant="body2" sx={{ 
                            color: isSelected ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
                            mb: 1
                          }}>
                            {getNetworkDescription(networkKey)}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" sx={{ 
                              color: isSelected ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)'
                            }}>
                              Chain ID: {networkConfig.chainId}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              color: isSelected ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)'
                            }}>
                              • {networkConfig.rpcUrl}
                            </Typography>
                          </Stack>
                        </Box>

                        <StatusChip
                          icon={statusInfo.icon}
                          label={statusInfo.label}
                          status={statusInfo.status}
                          size="small"
                        />
                      </Stack>
                    </NetworkButton>
                  </Box>
                </Fade>
              );
            })}
          </Stack>

          {/* Footer info */}
          <Box sx={{ mt: 4, p: 3, bgcolor: '#1a1a1a', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              💡 <strong>Note:</strong> Only devnet is currently available. Testnet and mainnet are coming soon.
            </Typography>
          </Box>
        </CardContent>
      </AvoCard>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </Container>
  );
};

export default AvoNetworkSelector;